import { useCallback, useState, useEffect } from 'react';
import { getEnvironment } from '@sunrun/sales-experience-shared';
import { PipelineProspect } from 'providers/Pipeline';
import { getInstallScheduleUrl } from 'providers/InstallScheduleUrl';

interface UseInstallScheduleProps {
  hybridToken: string;
}

export const useInstallSchedule = ({
  hybridToken,
}: UseInstallScheduleProps) => {
  const [showScheduleInstallModal, setShowScheduleInstallModal] =
    useState(false);
  const [currentPipelineProspect, setCurrentPipelineProspect] =
    useState<PipelineProspect | null>(null);
  const [isLoadingSchedulerLink, setIsLoadingSchedulerLink] = useState(false);
  const [selfSchedulerUuid, setSelfSchedulerUuid] = useState<
    string | undefined
  >(undefined);
  const [schedulerURLError, setSchedulerURLError] = useState('');
  const [serviceAppointmentId, setServiceAppointmentId] = useState<
    string | undefined
  >(undefined);
  const [fullSelfSchedulerUrl, setFullSelfSchedulerUrl] = useState('');

  const env = getEnvironment();

  // Generate full scheduler URL when selfSchedulerUuid changes
  useEffect(() => {
    if (selfSchedulerUuid) {
      setFullSelfSchedulerUrl(
        env === 'production'
          ? `https://www.scheduling.sunrun.com/?id=${selfSchedulerUuid}`
          : `https://www.scheduling.staging.ai.sunrun.com/?id=${selfSchedulerUuid}`,
      );
    }
  }, [env, selfSchedulerUuid]);

  const showInstallScheduleModalAndGetUrl = useCallback(
    (prospect: PipelineProspect) => {
      setIsLoadingSchedulerLink(true);
      setShowScheduleInstallModal(true);
      setCurrentPipelineProspect(prospect);
      setSchedulerURLError(''); // Clear any previous errors

      const serviceAppointmentId = prospect?.serviceAppointments?.filter(
        (appointment) => {
          return (
            appointment.status === 'Ready To Be Scheduled' &&
            appointment.recordType === 'Install'
          );
        },
      )[0]?.id;

      setServiceAppointmentId(serviceAppointmentId);

      getInstallScheduleUrl(hybridToken, serviceAppointmentId)
        .then((result) => {
          if (!result) {
            setSelfSchedulerUuid(undefined);
          } else {
            setSelfSchedulerUuid(result);
          }
          setIsLoadingSchedulerLink(false);
        })
        .catch((error) => {
          setIsLoadingSchedulerLink(false);
          setSchedulerURLError(error.message);
        });
    },
    [hybridToken],
  );

  const closeModal = useCallback(() => {
    setShowScheduleInstallModal(false);
  }, []);

  return {
    // Modal state
    showScheduleInstallModal,
    currentPipelineProspect,

    // Loading and error states
    isLoadingSchedulerLink,
    schedulerURLError,

    // Scheduler data
    selfSchedulerUuid,
    fullSelfSchedulerUrl,
    serviceAppointmentId,

    // Actions
    showInstallScheduleModalAndGetUrl,
    closeModal,
  };
};
